import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import DownloadIcon from "@atlaskit/icon/core/download";
import PhoneIcon from "@atlaskit/icon/core/phone";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {createFileRoute, useNavigate, useSearch} from "@tanstack/react-router";
import {useCallback, useEffect, useMemo, useState} from "react";
import {twJoin} from "tailwind-merge";
import {z} from "zod";
import {BottomDrawer} from "~/components/BottomDrawer";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {ExportDataDropdownItem} from "~/components/dataActions/ExportDataDropdownItem";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {EventAdditionalButtonFormModal} from "~/components/eventAdditionalButtonFormModal";
import {EventAdditionalButtonTrigger} from "~/components/eventAdditionalButtonTrigger";
import {EmotoradLeadDispositionModal} from "~/components/emotorad/emotoradLeadDispositionModal";
import {EventEditPanel} from "~/components/eventComponents/eventEditPanel";
import {EventViewPanel} from "~/components/eventComponents/eventViewPanel";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {createOttopilotListView, useEmotoradDispositionTrackerListCardPresentation} from "~/components/ottopilotDataViewer/emotoradListCardPresentation";
import {useListNotesPanel} from "~/components/notes/useListNotesPanel";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {orderColumnsLikeTable} from "~/components/ottopilotDataViewer/exportFromDb";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {resolveListCardStatusFromColumnConfig} from "~/components/ottopilotDataViewer/listStatusConfig";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {WorkspaceListCardCreateChrome} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/workspaceListCardCreateChrome";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useProductCatalogueTemplates, useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";

import {getAdditionalColumnOptions} from "~/domainModels/additionalColumns";

import type {OttopilotAdditionalButtonsConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {filterAdditionalButtonsVisibleForEvent} from "~/domainModels/eventTemplates";

import {getBobcardJourneyScopeFilterStatusIdsForEvents} from "~/domainModels/workspaceSpecific/bobcard";
import {DHIRAAGU__CALL_EVENT__ADDITIONAL_BUTTONS, isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {
    DULUX__APPOINTMENTS_EVENT__ADDITIONAL_BUTTONS,
    DULUX__CALLS_EVENT__ADDITIONAL_BUTTONS,
    DuluxEventTypes,
    isDuluxWorkspace,
} from "~/domainModels/workspaceSpecific/dulux";
import {
    AMARON__CALLS_EVENT__ADDITIONAL_BUTTONS,
    AMARON__SERVICE_EVENT__ADDITIONAL_BUTTONS,
    AmaronEventTypes,
    getAmaronJourneyScopeFilterStatusIdsForEvents,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_DISPOSITION_TRACKER_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    EmotoradCallStatusEnum,
    EventCallType,
    emotoradEventSyntheticFunction,
    EmotoradEventTypes,
    emotoradYesNoOptions,
    getEmotoradInputSourceSelectOptions,
    getEmotoradInputTypeSelectOptions,
    getEmotoradListEventCreateItems,
    getEmotoradListModuleConfig,
    getEmotoradJourneyScopeFilterStatusIdsForEvents,
    isEmotoradWorkspace,
    EmotoradYesNoEnum,
} from "~/domainModels/workspaceSpecific/emotorad";
import {isEmotoradJourneyModuleConfiguration} from "~/domainModels/workspaceSpecific/emotorad_old";
import {resolveWorkspaceListCreateItemsConfiguration} from "~/domainModels/listView/resolveWorkspaceListCreateItemsConfiguration";
import {
    EMOTORAD_DISPOSITION_TRACKER_FRONTEND_STATUSES,
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS,
    filterKanbanColumnsForModuleStatusId,
    getEmotoradDispositionTrackerKanbanColumnConfig,
    getEmotoradDispositionTrackerStatusNavConfig,
    getEmotoradModuleStatusNavValidStatusIds,
} from "~/domainModels/moduleStatusNav";
import {useSyncModuleStatusFilterFromSearch} from "~/hooks/useSyncModuleStatusFilterFromSearch";
import {useRequiredWorkspaceMobileNavigationConfiguration} from "~/hooks/workspaceConfiguration";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {EventKanbanBoardCard} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-event-kanban-board";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getEvent_ColumnValues, getEvents_Length, getEventsWithEventContainers, type EventWithEventContainer} from "~/utilities/apis/api";
import {getEventContainerColumnValues} from "~/utilities/apis/eventContainersApi";
import {EmotoradEventContainerTypes} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/EventContainers";
import type {BtlActivity} from "~/domainModels/btlActivities";
import {eventTableViewColumnsToOttopilotColumns} from "~/utilities/eventTableViewColumnsTransform";
import {
    resolveCalendarAllDayStyle,
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
} from "~/utilities/sharedCalendarConfig";
import {formatDateTime, getUserDisplayName, transformRowForExport} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    applyDispositionTrackerFrontendQuery,
    getDispositionTrackerFrontendColumnValues,
    getDispositionTrackerFrontendFilteredCount,
} from "./-disposition-tracker-filter-contract";
import {
    DISPOSITION_TRACKER_COLUMNS_MAP,
    DISPOSITION_TRACKER_DEFAULT_VISIBLE_FILTER_COLUMN_KEYS,
    DISPOSITION_TRACKER_TABLE_COLUMN_KEYS,
    getDispositionTrackerRuntimeValue as getDispositionTrackerRuntimeValueBase,
    intersectDispositionTrackerPreferredVisibleFilterKeysWithTableKeys,
    resolveDispositionTrackerFieldIdForKey,
    transformDispositionTrackerRowAtRuntime as transformDispositionTrackerRowAtRuntimeBase,
    type DispositionTrackerColumnKey,
} from "./-disposition-tracker-map";
import {buildDispositionTrackerViewPanelTemplate} from "./-disposition-tracker-view-panel";
import {requireEventTemplate} from "~/utilities/ottopilotSemanticColumns";

const eventsSearchSchema = z.object({
    view: z.enum(["list", "table", "kanban", "calendar", "create"]).optional().default("table").catch("table"),
    statusId: z.string().optional(),
});

const EVENTS_TABLE_VIEW_ID = uniqueUuid`7485111d-3520-4028-a593-9ee492a82c33`;
const EVENTS_LIST_VIEW_ID = uniqueUuid`1fcfcae8-0aad-4da2-8d8b-6ff48cb6de5e`;
const EVENTS_KANBAN_VIEW_ID = uniqueUuid`8baac944-642e-4856-b697-31a736e0d846`;
const EVENTS_CALENDAR_VIEW_ID = uniqueUuid`2b30fd87-8d4a-4e5b-ba0f-6af5cb75a16d`;
const EMOTORAD_DISPOSITION_TRACKER_TEMPLATE_ID = uniqueUuid`f3a1b2c4-d5e6-4f7a-8b9c-0d1e2f3a4b5c`;

/** Maps L2 slug (e.g. `future-intent`, `call-back-later`) to one of the five disposition-tracker statuses, or null. */
function mapEmotoradL2SlugToDispositionTrackerFrontendStatus(l2Slug: string): string | null {
    const s = l2Slug.trim().toLowerCase();
    if (s === "") {
        return null;
    }
    if (s === "call-back-later") {
        return EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.callBackLater;
    }
    if (s === "future-intent") {
        return EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.futureIntent;
    }
    if (s === "high-intent" || s === "high-interest") {
        return EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.highIntent;
    }
    if (s === "invalid") {
        return EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.invalid;
    }
    if (s === "lost") {
        return EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.lost;
    }
    return null;
}

/** After per-row transform, copy sparse event columns from same-journey siblings (input + follow-up DB columns). */
const EMOTORAD_DISPOSITION_TRACKER_SIBLING_MERGE_EVENT_COLUMN_IDS: Array<Uuid> = [
    EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CALL_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
];

const EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_DATE_SOURCE_COLUMN_IDS = [
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
] as const;

const EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_REQUIRED_SOURCE_COLUMN_IDS = [
    EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
] as const;

function mergeDispositionTrackerFollowUpColumnFromSiblings(
    merged: Record<string, unknown>,
    primary: EventWithEventContainer,
    group: Array<EventWithEventContainer>,
    targetColumnId: Uuid,
    sourceColumnIds: ReadonlyArray<Uuid>,
): void {
    const existing = merged[targetColumnId];
    if (existing !== undefined && existing !== null && String(existing).trim() !== "") {
        return;
    }
    for (const other of group) {
        if (other.id === primary.id) {
            continue;
        }
        for (const sourceColumnId of sourceColumnIds) {
            const value = other.additionalColumnValues[sourceColumnId];
            if (value !== undefined && value !== null && String(value).trim() !== "") {
                merged[targetColumnId] = value;
                return;
            }
        }
    }
}

function mergeEmotoradDispositionTrackerSiblingEventColumns(primary: EventWithEventContainer, group: Array<EventWithEventContainer>): EventWithEventContainer {
    const merged = {...primary.additionalColumnValues};
    for (const columnId of EMOTORAD_DISPOSITION_TRACKER_SIBLING_MERGE_EVENT_COLUMN_IDS) {
        const current = merged[columnId];
        if (current !== undefined && current !== null && String(current).trim() !== "") {
            continue;
        }
        for (const other of group) {
            if (other.id === primary.id) {
                continue;
            }
            const v = other.additionalColumnValues[columnId];
            if (v !== undefined && v !== null && String(v).trim() !== "") {
                merged[columnId] = v;
                break;
            }
        }
    }

    if (String(primary.templateId) === String(EmotoradEventTypes.call)) {
        mergeDispositionTrackerFollowUpColumnFromSiblings(
            merged,
            primary,
            group,
            EMOTORAD__EVENT__CALLS__FOLLOW_UP_DATE__COLUMN_ID,
            EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_DATE_SOURCE_COLUMN_IDS,
        );
        mergeDispositionTrackerFollowUpColumnFromSiblings(
            merged,
            primary,
            group,
            EMOTORAD__EVENT__CALLS__FOLLOW_UP_REQUIRED__COLUMN_ID,
            EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_REQUIRED_SOURCE_COLUMN_IDS,
        );
    } else if (String(primary.templateId) === String(EmotoradEventTypes.walkin)) {
        mergeDispositionTrackerFollowUpColumnFromSiblings(
            merged,
            primary,
            group,
            EMOTORAD__EVENT__WALKINS__FOLLOW_UP_DATE__COLUMN_ID,
            EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_DATE_SOURCE_COLUMN_IDS,
        );
        mergeDispositionTrackerFollowUpColumnFromSiblings(
            merged,
            primary,
            group,
            EMOTORAD__EVENT__WALKINS__FOLLOW_UP_REQUIRED__COLUMN_ID,
            EMOTORAD_DISPOSITION_TRACKER_FOLLOW_UP_REQUIRED_SOURCE_COLUMN_IDS,
        );
    }

    return {...primary, additionalColumnValues: merged};
}

/** Digits-only contact match for disposition tracker vs logged test ride journeys. */
function normalizeEmotoradDispositionTrackerContactForMatch(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).replace(/\D/g, "");
}

function isEmotoradDispositionTrackerYesLike(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    const s = String(value).trim();
    if (s === "") {
        return false;
    }
    if (s.toLowerCase() === "true") {
        return true;
    }
    return s === String(EmotoradYesNoEnum.YES);
}

function collectEmotoradLoggedTestRideLookup(testRideEvents: Array<EventWithEventContainer>): {
    journeyIdsWithLoggedTestRide: ReadonlySet<string>;
    normalizedContactsWithLoggedTestRide: ReadonlySet<string>;
} {
    const journeyIdsWithLoggedTestRide = new Set<string>();
    const normalizedContactsWithLoggedTestRide = new Set<string>();
    const loggedStatusId = String(EmotoradCallStatusEnum.LOGGED);
    for (const event of testRideEvents) {
        const status = event.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID];
        if (String(status) !== loggedStatusId) {
            continue;
        }
        journeyIdsWithLoggedTestRide.add(String(event.eventContainer.id));
        const normalized = normalizeEmotoradDispositionTrackerContactForMatch(
            event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
        );
        if (normalized.length > 0) {
            normalizedContactsWithLoggedTestRide.add(normalized);
        }
    }
    return {journeyIdsWithLoggedTestRide, normalizedContactsWithLoggedTestRide};
}

function resolveEventCalendarIsoStart(event: EventWithEventContainer, columnId: Uuid | undefined, fallback: string): string {
    if (columnId === undefined) {
        return fallback;
    }
    const fromEvent = event.additionalColumnValues[columnId];
    if (fromEvent !== null && fromEvent !== undefined && String(fromEvent).trim() !== "") {
        return String(fromEvent);
    }
    const fromJourney = event.eventContainer.additionalColumnValues[columnId];
    if (fromJourney !== null && fromJourney !== undefined && String(fromJourney).trim() !== "") {
        return String(fromJourney);
    }
    return fallback;
}

function toNonEmptyString(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const text = String(value).trim();
    return text === "" ? null : text;
}

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/disposition-tracker/",
)({
    validateSearch: (search: Record<string, unknown>) => eventsSearchSchema.parse(search),
    component: RouteComponent,
});

function RouteComponent() {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const eventTemplateId = EMOTORAD_DISPOSITION_TRACKER_TEMPLATE_ID;
    const dispositionTrackerPageTitle = "Disposition Tracker";
    const search = useSearch({
        from: "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/disposition-tracker/",
    });
    const isMobile = useCheckMobile();
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

    const journeysModuleConfiguration = useRequiredEventContainerTemplate(EmotoradEventContainerTypes.Journeys as Uuid);
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {workspace, workspaceCapabilities, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const currentUserTemplateIds = useMemo(() => {
        return new Set<Uuid>([workspaceUser.templateId]);
    }, [workspaceUser.templateId]);
    const eventsDataViewerQueryKeyPrefix = [uniqueUuid`7ca3b55c-03c6-431d-a657-81c6c9a35fe7`, EmotoradEventTypes.call, EmotoradEventTypes.walkin];
    const navigate = useNavigate();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const eventTemplates = useEventTemplates();
    const dispositionTrackerSemanticContext = useMemo(
        () => ({eventContainerTemplate: journeysModuleConfiguration, eventTemplates}),
        [eventTemplates, journeysModuleConfiguration],
    );
    const getDispositionTrackerRuntimeValue = useCallback(
        (event: EventWithEventContainer, key: DispositionTrackerColumnKey) =>
            getDispositionTrackerRuntimeValueBase(event, key, dispositionTrackerSemanticContext),
        [dispositionTrackerSemanticContext],
    );
    const transformDispositionTrackerRowAtRuntime = useCallback(
        (event: EventWithEventContainer) =>
            transformDispositionTrackerRowAtRuntimeBase(event, dispositionTrackerSemanticContext),
        [dispositionTrackerSemanticContext],
    );
    const products = useProductCatalogue() ?? [];
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const getProductCatalogueDisplayName = useCallback(
        (productCatalogueId: Uuid): string => {
            const product = products.find((item) => item.id === productCatalogueId);
            if (product === undefined) {
                return productCatalogueId;
            }
            const template = productCatalogueTemplates.find((item) => item.id === product.templateId);
            if (template === undefined) {
                return productCatalogueId;
            }
            const displayName = processOttopilotStringTemplate(template.instanceDisplayNameTemplate, product.additionalColumnValues);
            return displayName.trim() === "" ? productCatalogueId : displayName;
        },
        [productCatalogueTemplates, products],
    );
    const eventTemplate = useMemo(() => {
        return (
            eventTemplates.find((template) => template.id === eventTemplateId) ??
            eventTemplates.find((template) => template.id === (EmotoradEventTypes.call as Uuid))
        );
    }, [eventTemplateId, eventTemplates]);
    if (eventTemplate === undefined) {
        return null;
    }
    const callEventTemplate = eventTemplates.find((template) => template.id === (EmotoradEventTypes.call as Uuid));
    const walkinEventTemplate = eventTemplates.find((template) => template.id === (EmotoradEventTypes.walkin as Uuid));
    const journeyAdditionalColumnById = useMemo(
        () => new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column])),
        [getAdditionalColumns, journeysModuleConfiguration],
    );
    const callAdditionalColumnById = useMemo(
        () => new Map((getAdditionalColumns(callEventTemplate) ?? []).map((column) => [column.id, column])),
        [getAdditionalColumns, callEventTemplate],
    );
    const walkinAdditionalColumnById = useMemo(
        () => new Map((getAdditionalColumns(walkinEventTemplate) ?? []).map((column) => [column.id, column])),
        [getAdditionalColumns, walkinEventTemplate],
    );
    const formatDispositionTrackerDisplayValue = (key: DispositionTrackerColumnKey, rawValue: string): string => {
        if (rawValue === "") {
            // Synthetic status: blank when L2 does not map to one of the five labels (no "-" placeholder).
            if (key === "status") {
                return "";
            }
            return "-";
        }
        if (key === "lifecycleOwner" || key === "lastInputCreatedByName") {
            return getUserDisplayName(rawValue as Uuid, users, userTemplates);
        }
        if (key === "assignedDealer") {
            const trimmed = rawValue.trim();
            if (trimmed === "") {
                return "-";
            }
            const ou = organizationalUnits.find((unit) => unit.id === trimmed);
            if (ou !== undefined) {
                const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                if (ouTemplate !== undefined) {
                    const fromTemplate = processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                    if (fromTemplate.trim() !== "") {
                        return fromTemplate;
                    }
                }
                const dealershipName = ou.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID];
                if (typeof dealershipName === "string" && dealershipName.trim() !== "") {
                    return dealershipName;
                }
            }
            return trimmed;
        }
        if (key === "lastInputCreatedByRole") {
            const user = users.find((u) => String(u.id) === String(rawValue));
            if (user === undefined) {
                return "-";
            }
            return userTemplates.find((template) => template.id === user.templateId)?.displayName ?? "-";
        }
        if (key === "inputDateTime" || key === "followUpDate") {
            return formatDateTime(rawValue);
        }
        if (key === "followUpRequired" || key === "testRideTaken") {
            const lower = rawValue.toLowerCase();
            if (lower === "true") return "Yes";
            if (lower === "false") return "No";
            const yesNo = Object.values(emotoradYesNoOptions).find((o) => String(o.id) === rawValue);
            if (yesNo !== undefined) {
                return yesNo.displayName;
            }
        }
        if (key === "modelInterested") {
            const productMatch = products.find((product) => product.id === (rawValue as Uuid));
            if (productMatch !== undefined) {
                return getProductCatalogueDisplayName(productMatch.id);
            }
        }

        if (key === "inputType") {
            const inputOpt = getEmotoradInputTypeSelectOptions().find((o) => String(o.id) === rawValue);
            if (inputOpt !== undefined) {
                return inputOpt.displayName;
            }
            if (rawValue === EventCallType["inbound"]) {
                return "Inbound";
            }
            if (rawValue === EventCallType["outBound"]) {
                return "Outbound";
            }
        }
        if (key === "inputSource") {
            const srcOpt = getEmotoradInputSourceSelectOptions().find((o) => String(o.id) === rawValue);
            if (srcOpt !== undefined) {
                return srcOpt.displayName;
            }
        }

        const mapping = DISPOSITION_TRACKER_COLUMNS_MAP[key];
        const journeyColumn = mapping.journeyColumnId ? journeyAdditionalColumnById.get(mapping.journeyColumnId as Uuid) : undefined;
        if (
            journeyColumn?.type === UdoColumnType.userDefinedObject ||
            journeyColumn?.type === UdoColumnType.userDefinedObjects
        ) {
            const options = getAdditionalColumnOptions(journeyColumn, userDefinedObjectOptionsByTemplateId) ?? [];
            const labels = (Array.isArray(rawValue) ? rawValue : [rawValue])
                .map((value) => options.find((item) => item.id === value)?.displayName)
                .filter((label): label is string => label !== undefined);
            if (labels.length > 0) {
                return labels.join(", ");
            }
        }

        const eventColumn =
            (mapping.callColumnId ? callAdditionalColumnById.get(mapping.callColumnId as Uuid) : undefined) ??
            (mapping.walkinColumnId ? walkinAdditionalColumnById.get(mapping.walkinColumnId as Uuid) : undefined);
        if (
            eventColumn?.type === UdoColumnType.userDefinedObject ||
            eventColumn?.type === UdoColumnType.userDefinedObjects
        ) {
            const options = getAdditionalColumnOptions(eventColumn, userDefinedObjectOptionsByTemplateId) ?? [];
            const labels = (Array.isArray(rawValue) ? rawValue : [rawValue])
                .map((value) => options.find((item) => item.id === value)?.displayName)
                .filter((label): label is string => label !== undefined);
            if (labels.length > 0) {
                return labels.join(", ");
            }
        }

        return rawValue;
    };
    const resolveDispositionTrackerFrontendStatus = (event: EventWithEventContainer): string => {
        const l2Slug = String(getDispositionTrackerRuntimeValue(event, "l2Disposition") ?? "")
            .trim()
            .toLowerCase();
        return mapEmotoradL2SlugToDispositionTrackerFrontendStatus(l2Slug) ?? "";
    };
    const resolveDispositionStatusPillTokens = (value: string): {background: string; border: string} => {
        const normalized = value.trim().toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
        if (normalized === "call back later") {
            return {
                background: token("color.background.accent.blue.subtlest"),
                border: token("color.border.information"),
            };
        }
        if (normalized === "invalid") {
            return {
                background: token("color.background.accent.gray.subtlest"),
                border: token("color.border.input"),
            };
        }
        if (normalized === "future intent") {
            return {
                background: token("color.background.accent.yellow.subtlest"),
                border: token("color.border.accent.yellow"),
            };
        }
        if (normalized === "high intent") {
            return {
                background: token("color.background.accent.lime.subtlest"),
                border: token("color.border.success"),
            };
        }
        if (normalized === "lost") {
            return {
                background: token("color.background.accent.red.subtlest"),
                border: token("color.border.danger"),
            };
        }
        return {
            background: token("color.background.accent.gray.subtlest"),
            border: token("color.border.input"),
        };
    };
    /** Only include rows whose L2 maps to one of the five disposition-tracker status labels; unmapped L2 is excluded entirely. */
    const hasMappedDispositionTrackerL2Status = (event: EventWithEventContainer): boolean => {
        return mapEmotoradL2SlugToDispositionTrackerFrontendStatus(String(getDispositionTrackerRuntimeValue(event, "l2Disposition") ?? "")) !== null;
    };
    const effectiveEventTemplateForViewPanel = useMemo(() => {
        return buildDispositionTrackerViewPanelTemplate(getAdditionalColumns, {
            eventTemplate: eventTemplate,
            journeysModuleConfiguration: journeysModuleConfiguration,
        });
    }, [getAdditionalColumns, eventTemplate, journeysModuleConfiguration]);
    const effectiveEventTemplateForEditPanel = eventTemplate;
    const tableViewColumnsById = useMemo(
        () => new Map((eventTemplate.configuration.eventPage.tableViewColumns ?? []).map((column) => [column.id, column])),
        [eventTemplate.configuration.eventPage.tableViewColumns],
    );
    const resolveDispositionTrackerMappedField = (columnId: string): string => {
        if ((DISPOSITION_TRACKER_TABLE_COLUMN_KEYS as Array<string>).includes(columnId)) {
            return resolveDispositionTrackerFieldIdForKey(columnId as DispositionTrackerColumnKey);
        }
        for (const mapping of Object.values(DISPOSITION_TRACKER_COLUMNS_MAP)) {
            const candidates = [mapping.journeyColumnId, mapping.callColumnId, mapping.walkinColumnId];
            if (candidates.some((candidate) => String(candidate) === columnId)) {
                return String(mapping.journeyColumnId ?? mapping.callColumnId ?? mapping.walkinColumnId ?? columnId);
            }
        }
        return columnId;
    };
    const resolveTableColumnField = (columnId: string): string => {
        const tableViewField = tableViewColumnsById.get(columnId as Uuid)?.field;
        if (tableViewField !== undefined) {
            return tableViewField;
        }
        return resolveDispositionTrackerMappedField(columnId);
    };
    const resolveTableColumnConfig = (columnId: string) => {
        return tableViewColumnsById.get(columnId as Uuid);
    };
    const mapFilterConfigurationToFields = (filterConfiguration: FilterConfiguration): FilterConfiguration => ({
        ...filterConfiguration,
        lineItems: filterConfiguration.lineItems.map((lineItem) => ({
            ...lineItem,
            columnId: resolveTableColumnField(lineItem.columnId),
        })),
    });
    const mapSortConfigurationToFields = (sortConfiguration: SortConfiguration): SortConfiguration => ({
        ...sortConfiguration,
        lineItems: sortConfiguration.lineItems.map((lineItem) => ({
            ...lineItem,
            columnId: resolveTableColumnField(lineItem.columnId),
        })),
    });

    const hasEventCalendarConfig = eventTemplate.configuration.eventPage.calendarConfig !== undefined;
    const initialViewId =
        search.view === "list"
            ? EVENTS_LIST_VIEW_ID
            : search.view === "kanban"
              ? EVENTS_KANBAN_VIEW_ID
              : search.view === "calendar" && hasEventCalendarConfig
                ? EVENTS_CALENDAR_VIEW_ID
                : EVENTS_TABLE_VIEW_ID;
    // Sync controller view when URL search.view changes (e.g. sidebar Table/Kanban link)
    // This effect must run AFTER eventsDataViewerController is defined below, so we reference it via a ref.
    // We use a separate useEffect after the controller is created (see below, after controller definition).

    const getOuDisplayName = useMemo(() => {
        return (ouId: Uuid): string => {
            const ou = organizationalUnits.find((unit) => unit.id === ouId);
            if (ou === undefined) {
                return "-";
            }
            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (template === undefined) {
                return "-";
            }
            const nameColumn = template.additionalColumns.find((col) => col.displayName === "Name");
            if (nameColumn === undefined) {
                return "-";
            }
            const nameValue = ou.additionalColumnValues[nameColumn.id];
            return typeof nameValue === "string" && nameValue !== "" ? nameValue : "-";
        };
    }, [organizationalUnits, organizationalUnitTemplates]);

    const {showFlag} = useAnankeFlags();
    const [additionalButtonModal, setAdditionalButtonModal] = useState<{
        config: OttopilotAdditionalButtonsConfig;
        event: EventWithEventContainer;
    } | null>(null);
    const [selectedDispositionEvent, setSelectedDispositionEvent] = useState<EventWithEventContainer | null>(null);
    const {openNotesPanel, listNotesPanelChrome} = useListNotesPanel();
    const baseWorkspaceMobileNavigationConfiguration = useRequiredWorkspaceMobileNavigationConfiguration();
    const workspaceMobileNavigationConfiguration = useMemo(
        () =>
            resolveWorkspaceListCreateItemsConfiguration({
                workspace,
                configuration: baseWorkspaceMobileNavigationConfiguration,
            }),
        [workspace, baseWorkspaceMobileNavigationConfiguration],
    );
    const emotoradDispositionTrackerListConfig = getEmotoradListModuleConfig("dispositionTracker");
    const dispositionTrackerKanbanColumnConfig = useMemo(() => getEmotoradDispositionTrackerKanbanColumnConfig(), []);
    const dispositionTrackerKanbanColumnConfigForStatus = useMemo(
        () => filterKanbanColumnsForModuleStatusId(dispositionTrackerKanbanColumnConfig, search.statusId),
        [dispositionTrackerKanbanColumnConfig, search.statusId],
    );
    const listEventCreateDrawerItems = useMemo(() => {
        if (emotoradDispositionTrackerListConfig.includeEventCreateDrawer !== true) {
            return [] as const;
        }
        return getEmotoradListEventCreateItems(workspaceMobileNavigationConfiguration);
    }, [workspaceMobileNavigationConfiguration, emotoradDispositionTrackerListConfig.includeEventCreateDrawer]);
    const dispositionTrackerListPresentation = useEmotoradDispositionTrackerListCardPresentation({
        workspaceId: workspace.id,
        navigate,
        scheduleRoute: emotoradDispositionTrackerListConfig.scheduleRoute,
        listModuleConfig: emotoradDispositionTrackerListConfig,
        notesEventContainerSourceLabel: journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase,
        mapListFields: useCallback(
            (event: EventWithEventContainer) => {
                const displayName = formatDispositionTrackerDisplayValue(
                    "customerName",
                    String(getDispositionTrackerRuntimeValue(event, "customerName") ?? ""),
                );
                const phone = String(getDispositionTrackerRuntimeValue(event, "contactNumber") ?? "").trim();
                const statusName = resolveDispositionTrackerFrontendStatus(event);
                const assignedToRaw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID];
                const assignedOuLabel = typeof assignedToRaw === "string" && assignedToRaw.trim() !== "" ? getOuDisplayName(assignedToRaw as Uuid) : null;
                const assignedOuName = assignedOuLabel === null || assignedOuLabel === "-" || assignedOuLabel === "" ? null : assignedOuLabel;
                return {
                    displayName: displayName === "" ? "Disposition Tracker" : displayName,
                    phone: phone === "" ? null : phone,
                    status: resolveListCardStatusFromColumnConfig({
                        statusValue: statusName,
                        columnConfig: dispositionTrackerKanbanColumnConfig,
                        fallbackDisplayName: "Active",
                    }),
                    assignedOuName,
                    createdAt: event.createdAt,
                };
            },
            [dispositionTrackerKanbanColumnConfig, getOuDisplayName],
        ),
        onDisposeRequest: setSelectedDispositionEvent,
        openNotesPanel,
    });

    const eventSearchColumns = [EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID, EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
    const eventSearchPlaceholder = "Search customer by name or number";
    const eventScopeFilterConfig = {
        drawerTitle: "Type",
        selectionMode: "multi" as const,
        options: [
            {
                id: "5dcd64f5-e1f2-4c57-a374-b6fe3152c258" as Uuid,
                filterKey: `journey.${EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID}`,
                label: "Assigned to me",
                type: "User" as const,
                filterType: "equals" as const,
            },
            {
                id: "7a1dce23-0c9d-44fc-808a-5ed99b92c712" as Uuid,
                filterKey: "createdBy",
                label: "Created by me",
                type: "User" as const,
                filterType: "equals" as const,
            },
        ],
    };
    const isEmotoradJourneysCustomer = isEmotoradWorkspace(workspace) === true || isEmotoradJourneyModuleConfiguration(journeysModuleConfiguration);
    const isEmotoradSalesTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.sales as Uuid);
    const isEmotoradTestrideTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.testride as Uuid);
    const isEmotoradCallTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.call as Uuid);
    const isEmotoradWalkinTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.walkin as Uuid);
    const isEmotoradLeadsTemplate = isEmotoradJourneysCustomer === true && eventTemplate.id === (EmotoradEventTypes.lead as Uuid);
    const isEmotoradDispositionTrackerTemplate = isEmotoradJourneysCustomer === true;
    const shouldUseEmotoradExplicitColumns =
        isEmotoradSalesTemplate === true ||
        isEmotoradTestrideTemplate === true ||
        isEmotoradCallTemplate === true ||
        isEmotoradWalkinTemplate === true ||
        isEmotoradLeadsTemplate === true ||
        isEmotoradDispositionTrackerTemplate === true;
    const emotoradExplicitVisibleColumnIds = isEmotoradSalesTemplate
        ? EMOTORAD_SALES_EVENT_TABLE_VISIBLE_COLUMN_IDS
        : isEmotoradTestrideTemplate
          ? EMOTORAD_TESTRIDE_EVENT_TABLE_VISIBLE_COLUMN_IDS
          : isEmotoradCallTemplate
            ? EMOTORAD_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS
            : isEmotoradWalkinTemplate
              ? EMOTORAD_WALKINS_EVENT_TABLE_VISIBLE_COLUMN_IDS
              : isEmotoradLeadsTemplate
                ? EMOTORAD_LEADS_EVENT_TABLE_VISIBLE_COLUMN_IDS
                : isEmotoradDispositionTrackerTemplate
                  ? EMOTORAD_DISPOSITION_TRACKER_EVENT_TABLE_VISIBLE_COLUMN_IDS
                  : null;

    const dispositionTrackerDefaultVisibleFilterColumnIds = useMemo(() => {
        if (isEmotoradDispositionTrackerTemplate !== true) {
            return [] as Array<string>;
        }
        return intersectDispositionTrackerPreferredVisibleFilterKeysWithTableKeys(
            DISPOSITION_TRACKER_DEFAULT_VISIBLE_FILTER_COLUMN_KEYS,
            DISPOSITION_TRACKER_TABLE_COLUMN_KEYS,
        );
    }, [isEmotoradDispositionTrackerTemplate]);

    const eventJourneyScopeFilterConfig = useMemo(() => {
        if (journeysModuleConfiguration.configuration.statusColumnConfig === undefined) {
            return undefined;
        }
        const bobcardGroups = getBobcardJourneyScopeFilterStatusIdsForEvents(workspace);
        if (bobcardGroups !== null) {
            return {
                statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
                activeStatusIds: bobcardGroups.activeStatusIds,
                inactiveStatusIds: bobcardGroups.inactiveStatusIds,
            };
        }
        const amaronGroups = getAmaronJourneyScopeFilterStatusIdsForEvents(workspaceCapabilities);
        if (amaronGroups !== null) {
            return {
                statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
                activeStatusIds: amaronGroups.activeStatusIds,
                inactiveStatusIds: amaronGroups.inactiveStatusIds,
            };
        }
        const emotoradGroups = getEmotoradJourneyScopeFilterStatusIdsForEvents(workspace);
        if (emotoradGroups === null) {
            return undefined;
        }
        return {
            statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
            activeStatusIds: emotoradGroups.activeStatusIds,
            inactiveStatusIds: emotoradGroups.inactiveStatusIds,
        };
    }, [workspace, journeysModuleConfiguration.configuration.statusColumnConfig]);

    const additionalButtonConfig = useMemo(() => {
        if (eventTemplate.configuration.eventPage.additionalButtons !== undefined) {
            return eventTemplate.configuration.eventPage.additionalButtons;
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
                return DULUX__CALLS_EVENT__ADDITIONAL_BUTTONS;
            }
            if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
                return DULUX__APPOINTMENTS_EVENT__ADDITIONAL_BUTTONS;
            }
        }
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            if (eventTemplateId === (AmaronEventTypes.Calls as Uuid)) {
                return AMARON__CALLS_EVENT__ADDITIONAL_BUTTONS;
            }
            if (eventTemplateId === (AmaronEventTypes.Service as Uuid)) {
                return AMARON__SERVICE_EVENT__ADDITIONAL_BUTTONS;
            }
        }
        if (isDhiraaguWorkspace(workspace) === true) {
            return DHIRAAGU__CALL_EVENT__ADDITIONAL_BUTTONS;
        }
        return [];
    }, [eventTemplateId]);
    const eventCalendarConfig = eventTemplate.configuration.eventPage.calendarConfig;
    const effectiveKanbanConfig = eventTemplate.configuration.eventPage.kanbanBoardPageView ?? callEventTemplate?.configuration.eventPage.kanbanBoardPageView;
    const effectiveCalendarConfig = eventCalendarConfig ?? callEventTemplate?.configuration.eventPage.calendarConfig;
    const getEventCalendarType = useMemo(() => {
        if (effectiveCalendarConfig === undefined) {
            return (): string | undefined => undefined;
        }
        return (event: EventWithEventContainer): string | undefined => {
            const source = effectiveCalendarConfig.eventTypeSource;
            if (source.columnType === "OttopilotEvent") {
                if (source.columnId === "status") {
                    return String(event.status);
                }
                const value = event.additionalColumnValues[source.columnId as Uuid];
                return value === undefined || value === null ? undefined : String(value);
            }
            if (source.columnId === "status") {
                return String(event.eventContainer.status);
            }
            const value = event.eventContainer.additionalColumnValues[source.columnId as Uuid];
            return value === undefined || value === null ? undefined : String(value);
        };
    }, [effectiveCalendarConfig]);
    const eventCalendarLegendItems = useMemo(() => {
        if (isEmotoradDispositionTrackerTemplate) {
            return EMOTORAD_DISPOSITION_TRACKER_FRONTEND_STATUSES.map((status) => ({
                type: status,
                label: status,
            }));
        }
        if (effectiveCalendarConfig === undefined) {
            return [];
        }
        return resolveCalendarLegendItems(effectiveCalendarConfig, effectiveCalendarConfig);
    }, [effectiveCalendarConfig, isEmotoradDispositionTrackerTemplate]);
    const getEventCalendarTypeLabel = useMemo(() => {
        return (type: string) => {
            if (isEmotoradDispositionTrackerTemplate) {
                return type;
            }
            if (effectiveCalendarConfig === undefined) {
                return type;
            }
            return resolveCalendarEventTypeLabel(type, effectiveCalendarConfig, effectiveCalendarConfig, type);
        };
    }, [effectiveCalendarConfig, isEmotoradDispositionTrackerTemplate]);
    const getEventCalendarTypeColor = useMemo(() => {
        return (type: string | undefined) => {
            if (isEmotoradDispositionTrackerTemplate) {
                const tokens = resolveDispositionStatusPillTokens(String(type ?? ""));
                return {
                    backgroundColor: tokens.background,
                    borderColor: tokens.border,
                    textColor: "var(--ds-text)",
                };
            }
            if (effectiveCalendarConfig === undefined) {
                return {};
            }
            return resolveCalendarEventTypeColor(type, effectiveCalendarConfig, effectiveCalendarConfig);
        };
    }, [effectiveCalendarConfig, isEmotoradDispositionTrackerTemplate, resolveDispositionStatusPillTokens]);
    const eventCalendarAllDayStyle = useMemo(() => {
        if (effectiveCalendarConfig === undefined) {
            return {mode: "fixedGreen"} as const;
        }
        return resolveCalendarAllDayStyle(effectiveCalendarConfig, effectiveCalendarConfig);
    }, [effectiveCalendarConfig]);
    const dedupeDispositionTrackerCrossTypeRows = (
        rows: Array<EventWithEventContainer>,
        allEventsByJourneyId: Map<string, Array<EventWithEventContainer>>,
    ): Array<EventWithEventContainer> => {
        const resolveActivityTimestamp = (row: EventWithEventContainer): number => {
            const updatedMs = Date.parse(String(row.updatedAt));
            if (Number.isNaN(updatedMs) === false) {
                return updatedMs;
            }
            const createdMs = Date.parse(String(row.createdAt));
            return Number.isNaN(createdMs) ? 0 : createdMs;
        };
        const groups = new Map<string, Array<EventWithEventContainer>>();
        for (const row of rows) {
            const key = String(row.eventContainer.id);
            const existing = groups.get(key) ?? [];
            existing.push(row);
            groups.set(key, existing);
        }
        const output: Array<EventWithEventContainer> = [];
        for (const group of groups.values()) {
            const hasCall = group.some((row) => String(row.templateId) === String(EmotoradEventTypes.call));
            const hasWalkin = group.some((row) => String(row.templateId) === String(EmotoradEventTypes.walkin));
            if (hasCall === true && hasWalkin === true) {
                const latest = [...group].sort((a, b) => resolveActivityTimestamp(b) - resolveActivityTimestamp(a))[0];
                if (latest !== undefined) {
                    const journeyKey = String(latest.eventContainer.id);
                    const fullJourneyGroup = allEventsByJourneyId.get(journeyKey) ?? group;
                    output.push(mergeEmotoradDispositionTrackerSiblingEventColumns(latest, fullJourneyGroup));
                }
                continue;
            }
            output.push(...group);
        }
        return output;
    };
    const buildDispositionTrackerFrontendEvents = (
        events: Array<EventWithEventContainer>,
        loggedTestRideLookup: {
            journeyIdsWithLoggedTestRide: ReadonlySet<string>;
            normalizedContactsWithLoggedTestRide: ReadonlySet<string>;
        },
    ): Array<EventWithEventContainer> => {
        const runtimeTransformedEvents = events.map((event) => transformDispositionTrackerRowAtRuntime(event));
        const withTestRideTakenColumn = runtimeTransformedEvents.map((event) => {
            const prior = getDispositionTrackerRuntimeValue(event, "testRideTaken");
            const journeyId = String(event.eventContainer.id);
            const contactNorm = normalizeEmotoradDispositionTrackerContactForMatch(
                event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
            );
            const completedByJourney = loggedTestRideLookup.journeyIdsWithLoggedTestRide.has(journeyId);
            const completedByContact = contactNorm.length > 0 && loggedTestRideLookup.normalizedContactsWithLoggedTestRide.has(contactNorm);
            const testRideCompleted = completedByJourney === true || completedByContact === true;
            const finalYes = testRideCompleted === true || isEmotoradDispositionTrackerYesLike(prior) === true;
            const value = finalYes ? EmotoradYesNoEnum.YES : EmotoradYesNoEnum.NO;
            return {
                ...event,
                additionalColumnValues: {
                    ...event.additionalColumnValues,
                    [EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID]: value,
                    [EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID]: value,
                },
            };
        });
        /** Merge using every call/walk-in for the journey before L2 filter (scheduled rows have no mapped L2). */
        const allCallWalkinEventsByJourneyId = new Map<string, Array<EventWithEventContainer>>();
        for (const event of withTestRideTakenColumn) {
            const journeyKey = String(event.eventContainer.id);
            const bucket = allCallWalkinEventsByJourneyId.get(journeyKey) ?? [];
            bucket.push(event);
            allCallWalkinEventsByJourneyId.set(journeyKey, bucket);
        }
        const withJourneySiblingMergedColumns = withTestRideTakenColumn.map((event) =>
            mergeEmotoradDispositionTrackerSiblingEventColumns(event, allCallWalkinEventsByJourneyId.get(String(event.eventContainer.id)) ?? [event]),
        );
        const l2FilteredEvents = withJourneySiblingMergedColumns.filter((event) => hasMappedDispositionTrackerL2Status(event));
        const dedupedCrossTypeEvents = dedupeDispositionTrackerCrossTypeRows(l2FilteredEvents, allCallWalkinEventsByJourneyId);
        return dedupedCrossTypeEvents.map((event) => {
            const frontendStatus = resolveDispositionTrackerFrontendStatus(event);
            const inputDateTime = toNonEmptyString(getDispositionTrackerRuntimeValue(event, "inputDateTime"));
            const followUpDate = toNonEmptyString(getDispositionTrackerRuntimeValue(event, "followUpDate"));
            const startDate = inputDateTime ?? followUpDate ?? String(event.createdAt);
            const endDate = followUpDate ?? inputDateTime ?? startDate;
            const startMillis = Date.parse(startDate);
            const endMillis = Date.parse(endDate);
            const safeEndDate = Number.isNaN(startMillis) || Number.isNaN(endMillis) || endMillis >= startMillis ? endDate : startDate;
            return {
                ...event,
                additionalColumnValues: {
                    ...event.additionalColumnValues,
                    status: frontendStatus,
                },
                title: `${getDispositionTrackerRuntimeValue(event, "customerName") ?? "Disposition Tracker"} , ${getDispositionTrackerRuntimeValue(event, "contactNumber") ?? ""}`,
                startDate: startDate,
                endDate: safeEndDate,
                isAlldayEvent: false,
            };
        });
    };
    const fetchDispositionTrackerSourceEvents = async (): Promise<Array<EventWithEventContainer>> => {
        const eventContainerAdditionalColumnIds = getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id);
        const sharedRequest = {
            accessToken: heimdallUser.accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            sortConfiguration: {
                revisionId: generateUuid(),
                lineItems: [],
            },
            searchConfiguration: {
                revisionId: generateUuid(),
                searchValue: "",
                searchColumns: [],
            },
            limit: 10000,
            offset: 0,
            workspaceId: workspace.id,
            eventContainerAdditionalColumnIds: eventContainerAdditionalColumnIds,
        };
        const [callsWalkinsResult, testRidesResult] = await Promise.all([
            getEventsWithEventContainers({
                ...sharedRequest,
                filterConfiguration: {
                    revisionId: generateUuid(),
                    lineItems: [
                        {
                            columnId: "templateId",
                            operation: FilterOperation.in,
                            values: [String(EmotoradEventTypes.call), String(EmotoradEventTypes.walkin)],
                        },
                    ],
                },
            }),
            getEventsWithEventContainers({
                ...sharedRequest,
                filterConfiguration: {
                    revisionId: generateUuid(),
                    lineItems: [
                        {
                            columnId: "templateId",
                            operation: FilterOperation.equals,
                            value: String(EmotoradEventTypes.testride),
                        },
                    ],
                },
            }),
        ]);
        if (callsWalkinsResult.success !== true) {
            throw callsWalkinsResult.error;
        }
        if (testRidesResult.success !== true) {
            throw testRidesResult.error;
        }
        const syntheticCallsWalkins =
            isEmotoradJourneysCustomer === true
                ? callsWalkinsResult.data.map((event) => emotoradEventSyntheticFunction(event, requireEventTemplate(eventTemplates, event.templateId), journeysModuleConfiguration))
                : callsWalkinsResult.data;
        const syntheticTestRides =
            isEmotoradJourneysCustomer === true
                ? testRidesResult.data.map((event) => emotoradEventSyntheticFunction(event, requireEventTemplate(eventTemplates, event.templateId), journeysModuleConfiguration))
                : testRidesResult.data;
        const loggedTestRideLookup = collectEmotoradLoggedTestRideLookup(syntheticTestRides);
        return buildDispositionTrackerFrontendEvents(syntheticCallsWalkins, loggedTestRideLookup);
    };
    const eventsDataViewerController = useOttopilotDataViewerController<EventWithEventContainer>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Disposition",
            pluralSentenceCase: "Dispositions",
            singularLowerCase: "disposition",
            pluralLowerCase: "dispositions",
        },
        queryKeyPrefix: eventsDataViewerQueryKeyPrefix,
        calendarDataSource: "direct",
        hideEditPanelFooter: true,
        searchColumns: eventSearchColumns,
        searchPlaceholder: eventSearchPlaceholder,
        scopeFilterConfig: eventScopeFilterConfig,
        journeyScopeFilterConfig: eventJourneyScopeFilterConfig,
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            if (isEmotoradDispositionTrackerTemplate) {
                const sourceEvents = await fetchDispositionTrackerSourceEvents();
                return applyDispositionTrackerFrontendQuery({
                    events: sourceEvents,
                    filterConfiguration: filterConfiguration,
                    sortConfiguration: sortConfiguration,
                    searchConfiguration: searchConfiguration,
                    offset: offset,
                    limit: limit,
                    resolveFrontendStatus: resolveDispositionTrackerFrontendStatus,
                    semanticContext: dispositionTrackerSemanticContext,
                });
            }
            const mappedFilterConfiguration = mapFilterConfigurationToFields(filterConfiguration);
            const mappedSortConfiguration = mapSortConfigurationToFields(sortConfiguration);
            const eventsResult = await getEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    ...mappedFilterConfiguration,
                    lineItems: [
                        {
                            columnId: "templateId",
                            operation: FilterOperation.in,
                            values: [String(EmotoradEventTypes.call), String(EmotoradEventTypes.walkin)],
                        },
                        ...mappedFilterConfiguration.lineItems,
                    ],
                },
                sortConfiguration: mappedSortConfiguration,
                searchConfiguration: searchConfiguration,
                limit: limit,
                offset: offset,
                workspaceId: workspace.id,
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            });
            if (eventsResult.success !== true) {
                throw eventsResult.error;
            }
            const events = eventsResult.data.map((event) => emotoradEventSyntheticFunction(event, requireEventTemplate(eventTemplates, event.templateId), journeysModuleConfiguration));
            const runtimeTransformedEvents = isEmotoradDispositionTrackerTemplate
                ? events.map((event) => transformDispositionTrackerRowAtRuntime(event))
                : events;
            const l2FilteredEvents = isEmotoradDispositionTrackerTemplate
                ? runtimeTransformedEvents.filter((event) => hasMappedDispositionTrackerL2Status(event))
                : runtimeTransformedEvents;
            const kanbanReadyEvents = isEmotoradDispositionTrackerTemplate
                ? l2FilteredEvents.map((event) => {
                      const frontendStatus = resolveDispositionTrackerFrontendStatus(event);
                      return {
                          ...event,
                          additionalColumnValues: {
                              ...event.additionalColumnValues,
                              status: frontendStatus,
                          },
                      };
                  })
                : l2FilteredEvents;
            if (isEmotoradDispositionTrackerTemplate) {
                return kanbanReadyEvents.map((event) => {
                    const inputDateTime = toNonEmptyString(getDispositionTrackerRuntimeValue(event, "inputDateTime"));
                    const followUpDate = toNonEmptyString(getDispositionTrackerRuntimeValue(event, "followUpDate"));
                    const startDate = inputDateTime ?? followUpDate ?? String(event.createdAt);
                    const endDate = followUpDate ?? inputDateTime ?? startDate;
                    const startMillis = Date.parse(startDate);
                    const endMillis = Date.parse(endDate);
                    const safeEndDate = Number.isNaN(startMillis) || Number.isNaN(endMillis) || endMillis >= startMillis ? endDate : startDate;
                    return {
                        ...event,
                        title: String(getDispositionTrackerRuntimeValue(event, "customerName") ?? "Disposition Tracker"),
                        startDate: startDate,
                        endDate: safeEndDate,
                        isAlldayEvent: false,
                    };
                });
            }
            const calendarCfg = eventTemplate.configuration.eventPage.calendarConfig;
            if (
                calendarCfg !== undefined &&
                calendarCfg.calendarTitleOttopilotStringTemplate !== undefined &&
                calendarCfg.calendarStartDateColumnId !== undefined
            ) {
                return kanbanReadyEvents.map((event) => {
                    const merged: Record<Uuid, unknown> = {
                        ...event.eventContainer.additionalColumnValues,
                        ...event.additionalColumnValues,
                    };
                    const title = processOttopilotStringTemplate(calendarCfg.calendarTitleOttopilotStringTemplate ?? "", merged);
                    const startDate = resolveEventCalendarIsoStart(event, calendarCfg.calendarStartDateColumnId, event.createdAt);
                    return {
                        ...event,
                        title: title,
                        startDate: startDate,
                        endDate: startDate,
                        isAlldayEvent: false,
                    };
                });
            }

            return kanbanReadyEvents;
        },
        initialSortConfiguration: {
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "createdAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        canEditRow: (_event: EventWithEventContainer) => {
            return false;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            if (isEmotoradDispositionTrackerTemplate) {
                const sourceEvents = await fetchDispositionTrackerSourceEvents();
                const total = getDispositionTrackerFrontendFilteredCount({
                    events: sourceEvents,
                    filterConfiguration: filterConfiguration,
                    searchConfiguration: searchConfiguration,
                    resolveFrontendStatus: resolveDispositionTrackerFrontendStatus,
                    semanticContext: dispositionTrackerSemanticContext,
                });
                return total as Integer;
            }
            const mappedFilterConfiguration = mapFilterConfigurationToFields(filterConfiguration);
            const eventsLengthResult = await getEvents_Length({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    ...mappedFilterConfiguration,
                    lineItems: [
                        ...mappedFilterConfiguration.lineItems,
                        isEmotoradDispositionTrackerTemplate
                            ? {
                                  columnId: "templateId",
                                  operation: FilterOperation.in,
                                  values: [String(EmotoradEventTypes.call), String(EmotoradEventTypes.walkin)],
                              }
                            : {
                                  columnId: "templateId",
                                  operation: FilterOperation.equals,
                                  value: eventTemplateId,
                              },
                    ],
                },
                searchConfiguration: searchConfiguration,
                workspaceId: workspace.id,
                eventTemplateId: eventTemplateId,
                eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplate).map((col) => col.id),
                eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((col) => col.id),
            });
            if (eventsLengthResult.success !== true) {
                throw eventsLengthResult.error;
            }
            const eventsLength = eventsLengthResult.data;

            return eventsLength;
        },
        columnValuesQueryFn: async ({columnId}) => {
            if (isEmotoradDispositionTrackerTemplate) {
                if (columnId === "status") {
                    return [...EMOTORAD_DISPOSITION_TRACKER_FRONTEND_STATUSES];
                }
                const sourceEvents = await fetchDispositionTrackerSourceEvents();
                const directValues = getDispositionTrackerFrontendColumnValues({
                    events: sourceEvents,
                    columnId: columnId,
                    resolveFrontendStatus: resolveDispositionTrackerFrontendStatus,
                    semanticContext: dispositionTrackerSemanticContext,
                });
                if (directValues.length > 0) {
                    return directValues;
                }
                const resolvedColumnId = resolveTableColumnField(columnId);
                if (resolvedColumnId !== columnId) {
                    return getDispositionTrackerFrontendColumnValues({
                        events: sourceEvents,
                        columnId: resolvedColumnId,
                        resolveFrontendStatus: resolveDispositionTrackerFrontendStatus,
                        semanticContext: dispositionTrackerSemanticContext,
                    });
                }
                return [];
            }
            const tableViewColumn = resolveTableColumnConfig(columnId);
            const resolvedColumnId = resolveTableColumnField(columnId);

            if (tableViewColumn?.columnType === "OttopilotEvent") {
                const eventColumnValuesResult = await getEvent_ColumnValues({
                    workspaceId: workspace.id,
                    columnId: resolvedColumnId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                    templateId: eventTemplateId,
                });
                if (eventColumnValuesResult.success !== true) {
                    throw eventColumnValuesResult.error;
                }
                return eventColumnValuesResult.data;
            }

            if (tableViewColumn?.columnType === "OttopilotEventContainer") {
                const journeysColumnValuesResult = await getEventContainerColumnValues({
                    workspaceId: workspace.id,
                    column: resolvedColumnId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                    templateId: journeysModuleConfiguration.id,
                });
                if (journeysColumnValuesResult.success !== true) {
                    throw journeysColumnValuesResult.error;
                }
                return journeysColumnValuesResult.data.filter((value): value is string => value !== null);
            }

            const isEventColumn = getAdditionalColumns(eventTemplate).some((col) => col.id === resolvedColumnId);
            if (isEventColumn) {
                const eventColumnValuesResult = await getEvent_ColumnValues({
                    workspaceId: workspace.id,
                    columnId: resolvedColumnId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                    templateId: eventTemplateId,
                });
                if (eventColumnValuesResult.success !== true) {
                    throw eventColumnValuesResult.error;
                }
                return eventColumnValuesResult.data;
            }

            const journeysColumnValuesResult = await getEventContainerColumnValues({
                workspaceId: workspace.id,
                column: resolvedColumnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                templateId: journeysModuleConfiguration.id,
            });
            if (journeysColumnValuesResult.success !== true) {
                throw journeysColumnValuesResult.error;
            }
            return journeysColumnValuesResult.data.filter((value): value is string => value !== null);
        },
        columns: {
            revisionId: uniqueUuid`70d624af-ff1d-455b-94b6-16ac8afb2fa5`,
            lineItems: ((): Array<OttopilotColumn<EventWithEventContainer>> => {
                const generatedLineItems: Array<OttopilotColumn<EventWithEventContainer>> =
                    eventTemplate.configuration.eventPage.tableViewColumns !== undefined && shouldUseEmotoradExplicitColumns !== true
                        ? eventTableViewColumnsToOttopilotColumns(getAdditionalColumns, eventTemplate.configuration.eventPage.tableViewColumns, {
                              userDefinedObjectOptionsByTemplateId,
                              users: users,
                              userTemplates: userTemplates,
                              btlActivities: btlActivities,
                              formatDateTime: formatDateTime,
                              getOuDisplayName: getOuDisplayName,
                              getProductCatalogueDisplayName,
                              eventTemplate: eventTemplate,
                              eventContainerAdditionalColumns: getAdditionalColumns(journeysModuleConfiguration),
                          })
                        : [
                              ...getAdditionalColumns(journeysModuleConfiguration).map((column): OttopilotColumn<EventWithEventContainer> => {
                                  // Get the first eventTemplate as a fallback (required by type but may not be used by all column types)
                                  const fallbackEventTemplate = eventTemplates[0];
                                  if (fallbackEventTemplate === undefined) {
                                      throw new Error("No event templates available");
                                  }

                                  const isTimestamp = column.type === UdoColumnType.timestamp;

                                  if (isTimestamp) {
                                      return {
                                          id: column.id,
                                          type: OttopilotColumnType.iso8601DateTime,
                                          displayName: column.displayName,
                                          valueGetter: (event) => {
                                              const value = event.eventContainer.additionalColumnValues[column.id];
                                              // Return undefined if value is null/undefined, otherwise cast to Iso8601DateTime
                                              return (value ?? undefined) as any;
                                          },
                                          valueFormatter: (value) => formatDateTime(value),
                                          sortingCapability: true,
                                          filteringCapability_contains: true,
                                      };
                                  }

                                  return {
                                      id: column.id,
                                      // TODO: This is definitely incorrect
                                      type: OttopilotColumnType.string,
                                      displayName: column.displayName,
                                      valueGetter: (event) => {
                                          return String(event.eventContainer.additionalColumnValues[column.id] ?? "");
                                      },
                                      valueFormatter: (value) => {
                                          const stringValueGetter = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter;
                                          return stringValueGetter({
                                              value: value,
                                              users: users,
                                              userTemplates: userTemplates,
                                              organizationalUnits: organizationalUnits,
                                              organizationalUnitTemplates: organizationalUnitTemplates,
                                              products: products,
                                          });
                                      },
                                      sortingCapability: true,
                                      filteringCapability_contains: true,
                                  };
                              }),
                              {
                                  id: "id",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "ID",
                                  valueGetter: (event) => event.id,
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "createdBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Created by",
                                  valueGetter: (event) => event.createdBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "createdAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Created at",
                                  valueGetter: (event) => event.createdAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "updatedBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Updated by",
                                  valueGetter: (event) => event.updatedBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                                  // filteringCapability_in: true,
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "updatedAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Updated at",
                                  valueGetter: (event) => event.updatedAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "templateId",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Type",
                                  valueGetter: (event) => event.templateId,
                                  valueFormatter: (value) => eventTemplates.find((eventTemplate) => eventTemplate.id === value)?.displayName ?? "?",
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              {
                                  id: "actionButton",
                                  type: OttopilotColumnType.string,
                                  displayName: "Actions",
                                  valueGetter: () => "",
                                  sortingCapability: false,
                                  filteringCapability_contains: false,
                              },
                              ...getAdditionalColumns(eventTemplate)
                                  .filter((eventColumn) => {
                                      // Filter out event columns that have the same display name as journey columns
                                      const hasMatchingJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some(
                                          (journeyColumn) => journeyColumn.displayName === eventColumn.displayName,
                                      );
                                      return !hasMatchingJourneyColumn;
                                  })
                                  .map((column): OttopilotColumn<EventWithEventContainer> => {
                                      const isTimestamp = column.type === UdoColumnType.timestamp;

                                      if (isTimestamp) {
                                          return {
                                              id: column.id,
                                              type: OttopilotColumnType.iso8601DateTime,
                                              displayName: column.displayName,
                                              valueGetter: (event) => {
                                                  const value = event?.additionalColumnValues[column.id];
                                                  // Return undefined if value is null/undefined, otherwise cast to Iso8601DateTime
                                                  return (value ?? undefined) as any;
                                              },
                                              valueFormatter: (value) => formatDateTime(value),
                                              sortingCapability: true,
                                              filteringCapability_contains: true,
                                          };
                                      }
                                      if (column.id === "a71e8f2a-7b91-4d3c-9e04-52a8f1c3d701") {
                                          console.log("sent from here");
                                      }
                                      return {
                                          id: column.id,
                                          // TODO: This is definitely incorrect
                                          type: OttopilotColumnType.string,
                                          displayName: column.displayName,
                                          valueGetter: (event) => {
                                              return event?.additionalColumnValues[column.id] as string;
                                          },
                                          valueFormatter: (value) => {
                                              const stringValueGetter = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter;
                                              return stringValueGetter({
                                                  value: value as Uuid,
                                                  users: users,
                                                  userTemplates: userTemplates,
                                                  organizationalUnits: organizationalUnits,
                                                  organizationalUnitTemplates: organizationalUnitTemplates,
                                                  products: products,
                                              });
                                          },
                                          sortingCapability: true,
                                          filteringCapability_contains: true,
                                      };
                                  }),
                          ];

                if (emotoradExplicitVisibleColumnIds === null) {
                    return generatedLineItems;
                }

                const lineItemsWithEmotoradCallSynthetic = isEmotoradCallTemplate
                    ? [
                          ...generatedLineItems,
                          {
                              id: EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                      ]
                    : generatedLineItems;

                const lineItemsWithEmotoradWalkinSynthetic = isEmotoradWalkinTemplate
                    ? [
                          ...lineItemsWithEmotoradCallSynthetic,
                          {
                              id: EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                      ]
                    : lineItemsWithEmotoradCallSynthetic;

                const lineItemsWithEmotoradDispositionSynthetic = isEmotoradDispositionTrackerTemplate
                    ? [
                          ...lineItemsWithEmotoradWalkinSynthetic,
                          {
                              id: EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L1 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__DISPOSITION_TRACKER__L1_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L2 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__DISPOSITION_TRACKER__L2_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                          {
                              id: EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID,
                              type: OttopilotColumnType.string,
                              displayName: "L3 Disposition",
                              valueGetter: (event: EventWithEventContainer) =>
                                  String(event.additionalColumnValues[EMOTORAD__EVENT__DISPOSITION_TRACKER__L3_DISPOSITION__COLUMN_ID] ?? ""),
                              sortingCapability: true,
                              filteringCapability_contains: true,
                          },
                      ]
                    : lineItemsWithEmotoradWalkinSynthetic;

                const explicitById = new Map<string, OttopilotColumn<EventWithEventContainer>>(
                    lineItemsWithEmotoradDispositionSynthetic.map((column) => [column.id, column as OttopilotColumn<EventWithEventContainer>]),
                );
                const eventAdditionalColumnById = new Map(getAdditionalColumns(eventTemplate).map((column) => [column.id, column]));
                const journeyAdditionalColumnById = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]));
                const buildFallbackExplicitColumn = (id: string): OttopilotColumn<EventWithEventContainer> => {
                    if (id === "updatedBy" || id === "createdBy") {
                        return {
                            id: id,
                            type: OttopilotColumnType.uuid,
                            displayName: id === "updatedBy" ? "Updated by" : "Created by",
                            valueGetter: (event) => (id === "updatedBy" ? event.updatedBy : event.createdBy),
                            valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                            sortingCapability: false,
                            filteringCapability_contains: false,
                        };
                    }
                    if (id === "updatedAt" || id === "createdAt") {
                        return {
                            id: id,
                            type: OttopilotColumnType.iso8601DateTime,
                            displayName: id === "updatedAt" ? "Updated at" : "Created at",
                            valueGetter: (event) => (id === "updatedAt" ? event.updatedAt : event.createdAt),
                            valueFormatter: (value) => formatDateTime(value),
                            sortingCapability: true,
                            filteringCapability_contains: false,
                        };
                    }
                    const eventColumn = eventAdditionalColumnById.get(id as Uuid);
                    if (eventColumn !== undefined) {
                        return {
                            id: id,
                            type: OttopilotColumnType.string,
                            displayName: eventColumn.displayName,
                            valueGetter: (event) => String(event.additionalColumnValues[id as Uuid] ?? ""),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        };
                    }
                    const journeyColumn = journeyAdditionalColumnById.get(id as Uuid);
                    if (journeyColumn !== undefined) {
                        return {
                            id: id,
                            type: OttopilotColumnType.string,
                            displayName: journeyColumn.displayName,
                            valueGetter: (event) => String(event.eventContainer.additionalColumnValues[id as Uuid] ?? ""),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        };
                    }
                    return {
                        id: id,
                        type: OttopilotColumnType.string,
                        displayName: id,
                        valueGetter: (event) => String(event.additionalColumnValues[id as Uuid] ?? ""),
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    };
                };
                if (isEmotoradDispositionTrackerTemplate) {
                    return DISPOSITION_TRACKER_TABLE_COLUMN_KEYS.map((key): OttopilotColumn<EventWithEventContainer> => ({
                        id: key,
                        type: OttopilotColumnType.string,
                        displayName: DISPOSITION_TRACKER_COLUMNS_MAP[key].displayName,
                        valueGetter: (event: EventWithEventContainer) =>
                            key === "status" ? resolveDispositionTrackerFrontendStatus(event) : String(getDispositionTrackerRuntimeValue(event, key) ?? ""),
                        valueFormatter: (value) => formatDispositionTrackerDisplayValue(key, String(value ?? "")),
                        cellRenderer:
                            key === "status"
                                ? (event: EventWithEventContainer) => {
                                      const text = resolveDispositionTrackerFrontendStatus(event);
                                      if (text.trim() === "") {
                                          return null;
                                      }
                                      const pill = resolveDispositionStatusPillTokens(text);
                                      return (
                                          <span
                                              style={{
                                                  background: pill.background,
                                                  border: `1px solid ${pill.border}`,
                                                  borderRadius: "4px",
                                                  padding: "2px 6px",
                                                  fontSize: "14px",
                                                  lineHeight: "16px",
                                                  display: "inline-flex",
                                              }}
                                          >
                                              {text}
                                          </span>
                                      );
                                  }
                                : undefined,
                        sortingCapability: true,
                        filteringCapability_contains: true,
                    }));
                }
                return emotoradExplicitVisibleColumnIds.map((id) => explicitById.get(id) ?? buildFallbackExplicitColumn(id));
            })(),
        },
        views: [
            ...(isEmotoradDispositionTrackerTemplate === true && emotoradDispositionTrackerListConfig.enabled === true
                ? [
                      createOttopilotListView<EventWithEventContainer>(EVENTS_LIST_VIEW_ID, (event, listCardContext) =>
                          dispositionTrackerListPresentation.renderListCard(event, listCardContext),
                      ),
                  ]
                : []),
            {
                id: uniqueUuid`7485111d-3520-4028-a593-9ee492a82c33`,
                viewType: ViewType.table,
                visibleColumns: isEmotoradDispositionTrackerTemplate
                    ? DISPOSITION_TRACKER_TABLE_COLUMN_KEYS
                    : [
                          ...getAdditionalColumns(eventTemplate)
                              .filter((eventColumn) => {
                                  // Filter out event columns that have the same display name as journey columns
                                  const hasMatchingJourneyColumn = getAdditionalColumns(journeysModuleConfiguration).some(
                                      (journeyColumn) => journeyColumn.displayName === eventColumn.displayName,
                                  );
                                  return !hasMatchingJourneyColumn;
                              })
                              .map((column) => column.id),
                          ...getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id),
                          "status",
                          "disposition",
                          "createdBy",
                          "createdAt",
                          "updatedBy",
                          "updatedAt",
                      ],
                otherArgs: {
                    // Removed navigation - view/edit buttons will open panels instead
                    // rowClass: "cursor-pointer",
                    // onRowClicked: (event: {data: EventWithEventContainer}) => {
                    //     // Don't navigate if user clicked on the action column
                    //     if (event.event.preventOnRowClicked === true) {
                    //         return;
                    //     }
                    //
                    //     navigate({
                    //         to: `/w/${workspace.id}/ottopilot/event-containers/${event.data.eventContainer.id}/events/${event.data.id}`,
                    //     });
                    // },
                },
            },
            {
                id: uniqueUuid`8baac944-642e-4856-b697-31a736e0d846`,
                viewType: ViewType.kanbanBoard,
                cardRenderer: (event: EventWithEventContainer) =>
                    isEmotoradDispositionTrackerTemplate ? (
                        <div
                            className={twJoin(`w-full`, "shadow-sm", "relative group")}
                            style={{
                                borderRadius: token("radius.small"),
                                background: token("color.icon.inverse"),
                                boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                            }}
                        >
                            <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150">
                                <div className="grid grid-flow-col gap-ds-space-100 justify-between">
                                    <div className="line-clamp-1 overflow-hidden">
                                        <div
                                            className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                            style={{fontFamily: "Inter", color: token("color.text")}}
                                        >
                                            {formatDispositionTrackerDisplayValue(
                                                "customerName",
                                                String(getDispositionTrackerRuntimeValue(event, "customerName") ?? ""),
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-ds-space-100 items-center">
                                        {(() => {
                                            const contactNumber = String(getDispositionTrackerRuntimeValue(event, "contactNumber") ?? "").trim();
                                            return contactNumber === "" ? (
                                                <PhoneIcon
                                                    label="phone"
                                                    color={token("color.icon")}
                                                />
                                            ) : (
                                                <a
                                                    href={`tel:${contactNumber}`}
                                                    aria-label="Call"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <PhoneIcon
                                                        label="phone"
                                                        color={token("color.icon")}
                                                    />
                                                </a>
                                            );
                                        })()}
                                        <ShowMoreHorizontalIcon
                                            label="more"
                                            color={token("color.icon")}
                                        />
                                    </div>
                                </div>
                                <VerticalSpacer className="h-ds-space-100" />
                                <div className="line-clamp-1 text-right grid grid-flow-col justify-start gap-x-ds-space-075 items-center">
                                    <CalendarIcon
                                        label=""
                                        color={token("color.icon")}
                                    />
                                    <span
                                        className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                                        style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                                    >
                                        {formatDispositionTrackerDisplayValue(
                                            "inputDateTime",
                                            String(getDispositionTrackerRuntimeValue(event, "inputDateTime") ?? ""),
                                        )}
                                    </span>
                                </div>
                                <VerticalSpacer className="h-ds-space-150" />
                                <div className="w-full h-px bg-ds-border" />
                                <VerticalSpacer className="h-ds-space-150" />
                                <div className="grid gap-ds-space-150">
                                    <div className="grid grid-cols-2 gap-ds-space-150">
                                        <div>
                                            <div className="grid grid-flow-col justify-start items-center gap-x-ds-space-050">
                                                <RoadmapIcon
                                                    label=""
                                                    color={token("color.icon.subtle")}
                                                />
                                                <div
                                                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                                                    style={{fontFamily: "Inter", color: token("color.text.subtlest")}}
                                                >
                                                    L1 Disposition
                                                </div>
                                            </div>
                                            <div
                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis"
                                                style={{fontFamily: "Inter", color: token("color.text")}}
                                            >
                                                {formatDispositionTrackerDisplayValue(
                                                    "l1Disposition",
                                                    String(getDispositionTrackerRuntimeValue(event, "l1Disposition") ?? ""),
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="grid grid-flow-col justify-start items-center gap-x-ds-space-050">
                                                <RoadmapIcon
                                                    label=""
                                                    color={token("color.icon.subtle")}
                                                />
                                                <div
                                                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                                                    style={{fontFamily: "Inter", color: token("color.text.subtlest")}}
                                                >
                                                    L2 Disposition
                                                </div>
                                            </div>
                                            <div
                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis"
                                                style={{fontFamily: "Inter", color: token("color.text")}}
                                            >
                                                {formatDispositionTrackerDisplayValue(
                                                    "l2Disposition",
                                                    String(getDispositionTrackerRuntimeValue(event, "l2Disposition") ?? ""),
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-ds-space-150">
                                        <div>
                                            <div className="grid grid-flow-col justify-start items-center gap-x-ds-space-050">
                                                <RoadmapIcon
                                                    label=""
                                                    color={token("color.icon.subtle")}
                                                />
                                                <div
                                                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                                                    style={{fontFamily: "Inter", color: token("color.text.subtlest")}}
                                                >
                                                    L3 Disposition
                                                </div>
                                            </div>
                                            <div
                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis"
                                                style={{fontFamily: "Inter", color: token("color.text")}}
                                            >
                                                {formatDispositionTrackerDisplayValue(
                                                    "l3Disposition",
                                                    String(getDispositionTrackerRuntimeValue(event, "l3Disposition") ?? ""),
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div
                                                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                                                style={{fontFamily: "Inter", color: token("color.text.subtlest")}}
                                            >
                                                Input source
                                            </div>
                                            <div
                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis"
                                                style={{fontFamily: "Inter", color: token("color.text")}}
                                            >
                                                {formatDispositionTrackerDisplayValue(
                                                    "inputSource",
                                                    String(getDispositionTrackerRuntimeValue(event, "inputSource") ?? ""),
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <EventKanbanBoardCard
                            key={event.id}
                            event={event}
                            onRequestAdditionalButtonModal={(payload) => {
                                setAdditionalButtonModal(payload);
                            }}
                            dataViewerQueryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                        />
                    ),
                columnConfig: isEmotoradDispositionTrackerTemplate
                    ? dispositionTrackerKanbanColumnConfigForStatus
                    : (effectiveKanbanConfig?.columnConfig ?? []),
                filterFieldId: isEmotoradDispositionTrackerTemplate ? "status" : (effectiveKanbanConfig?.filterFieldId ?? "status"),
            },
            ...(effectiveCalendarConfig !== undefined
                ? [
                      {
                          id: EVENTS_CALENDAR_VIEW_ID,
                          viewType: ViewType.calendar as const,
                          getEventType: isEmotoradDispositionTrackerTemplate
                              ? (event: EventWithEventContainer) => resolveDispositionTrackerFrontendStatus(event)
                              : getEventCalendarType,
                          legendItems: eventCalendarLegendItems,
                          getEventTypeLabel: getEventCalendarTypeLabel,
                          getEventColor: getEventCalendarTypeColor,
                          allDayStyle: eventCalendarAllDayStyle,
                          startTimeOnly: isEmotoradDispositionTrackerTemplate ? true : undefined,
                          use24HourTimeAxis: isEmotoradDispositionTrackerTemplate === true,
                          disableCreateEventButton: isEmotoradDispositionTrackerTemplate === true,
                      },
                  ]
                : []),
            // {
            //     id: uniqueUuid`61070cb1-ffcd-48a3-88f5-eaaa5e849168`,
            //     viewType: ViewType.cards,
            //     cardRenderer: (event) => (
            //         <EventCard
            //             event={event}
            //             wrapInsideLink={true}
            //         />
            //     ),
            // },
        ],
        initialViewId: initialViewId,
        initialVisibleFilters: {
            revisionId: uniqueUuid`db459f43-34c9-4d3d-9600-456c1f441ffb`,
            lineItems:
                isEmotoradDispositionTrackerTemplate === true && dispositionTrackerDefaultVisibleFilterColumnIds.length > 0
                    ? dispositionTrackerDefaultVisibleFilterColumnIds
                    : [getAdditionalColumns(journeysModuleConfiguration)[0]?.id!].filter(Boolean),
        },
        listCardChrome: (
            <>
                {isEmotoradDispositionTrackerTemplate === true && selectedDispositionEvent !== null ? (
                    <EmotoradLeadDispositionModal
                        event={selectedDispositionEvent}
                        eventContainerTemplate={journeysModuleConfiguration}
                        queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                        onClose={() => {
                            setSelectedDispositionEvent(null);
                        }}
                    />
                ) : null}
                {listNotesPanelChrome}
                {isEmotoradDispositionTrackerTemplate === true &&
                emotoradDispositionTrackerListConfig.includeEventCreateDrawer === true &&
                dispositionTrackerListPresentation.footerBindings.selectedDispositionId !== null ? (
                    <WorkspaceListCardCreateChrome
                        opened={dispositionTrackerListPresentation.footerBindings.selectedDispositionId !== null}
                        onClose={() => {
                            dispositionTrackerListPresentation.footerBindings.setSelectedDispositoinId(null);
                            dispositionTrackerListPresentation.footerBindings.setSelectedCreateJourneyId(null);
                        }}
                        workspaceId={workspace.id}
                        items={listEventCreateDrawerItems}
                        eventContainerId={dispositionTrackerListPresentation.footerBindings.selectedCreateJourneyId}
                    />
                ) : null}
            </>
        ),
        readPanelComponent: (row, isExpanded) => (
            <EventViewPanel
                row={row}
                isExpanded={isExpanded}
                eventTemplate={effectiveEventTemplateForViewPanel}
                journeysModuleConfiguration={journeysModuleConfiguration}
                eventTemplates={eventTemplates}
            />
        ),
        updatePanelComponent: (row, isExpanded) => {
            const effectiveEditPanel = effectiveEventTemplateForEditPanel.configuration.eventPage.editPanel;
            if (effectiveEditPanel !== undefined && effectiveEditPanel.columnGrouping.length === 0) {
                return null;
            }
            return (
                <EventEditPanel
                    row={row}
                    isExpanded={isExpanded}
                    eventTemplate={effectiveEventTemplateForEditPanel}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    eventTemplates={eventTemplates}
                />
            );
        },
        rowEngagementDropdownOptions: (row) => {
            const exportColumns = orderColumnsLikeTable(
                eventsDataViewerController.columns.lineItems.filter((col) => col.id !== "id" && col.id !== "actionButton"),
                eventsDataViewerController.tableColumnConfig?.orderedVisibleColumnIds,
            );

            const dataToExport = transformRowForExport(row, exportColumns);

            const visibleAdditionalConfigs = filterAdditionalButtonsVisibleForEvent({
                additionalButtons: additionalButtonConfig,
                event: row,
                currentUserTemplateIds: currentUserTemplateIds,
            });

            return [
                <ExportDataDropdownItem
                    key="export"

                    isVisible={false}
                />,
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
                ...visibleAdditionalConfigs.map((config) => (
                    <EventAdditionalButtonTrigger
                        key={config.id}
                        config={config}
                        event={row}
                        layout="dropdownItem"
                        currentUserTemplateIds={currentUserTemplateIds}
                        workspaceId={workspace.id}
                        onOpenModal={(payload) => {
                            setAdditionalButtonModal(payload);
                        }}
                    />
                )),
            ];
        },
    });

    const emotoradDispositionTrackerStatusNavConfig = useMemo(() => {
        if (isEmotoradDispositionTrackerTemplate !== true) {
            return null;
        }
        return getEmotoradDispositionTrackerStatusNavConfig();
    }, [isEmotoradDispositionTrackerTemplate]);

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: emotoradDispositionTrackerStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: eventsDataViewerController.setFilterConfiguration,
        enabled: isEmotoradDispositionTrackerTemplate === true && emotoradDispositionTrackerStatusNavConfig !== null,
        validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradDispositionTrackerStatusNavConfig),
    });

    // Sync view based on URL query param (search.view from useSearch updates when sidebar Table/Kanban link navigates)
    useEffect(() => {
        if (search.view === "create" && isDhiraaguWorkspace(workspace) === true) {
            navigate({
                to: "/w/$workspaceId/ottopilot/dhiraagu/journey/create",
                params: {workspaceId: workspace.id},
            });
            return;
        }
        const viewId = search.view === "kanban" ? EVENTS_KANBAN_VIEW_ID : search.view === "calendar" ? EVENTS_CALENDAR_VIEW_ID : EVENTS_TABLE_VIEW_ID;
        eventsDataViewerController.setCurrentViewId(viewId);
    }, [search.view, workspace.id, navigate]);

    useEffect(() => {
        if (isEmotoradDispositionTrackerTemplate !== true || dispositionTrackerDefaultVisibleFilterColumnIds.length === 0) {
            return;
        }
        eventsDataViewerController.setVisibleFilters({
            revisionId: uniqueUuid`f1a2b3c4-d5e6-4788-a9b0-c1d2e3f4a5b6`,
            lineItems: dispositionTrackerDefaultVisibleFilterColumnIds,
        });
    }, [isEmotoradDispositionTrackerTemplate, dispositionTrackerDefaultVisibleFilterColumnIds, eventsDataViewerController.setVisibleFilters]);

    const {
        onExportPng: handleExportAllRowsAsPng,
        onExportPdf: handleExportAllRowsAsPdf,
    } = useOttopilotExportActions({
        controller: eventsDataViewerController,

        pngFilename: `events-${eventTemplateId}.png`,
        pdfFilename: `events-${eventTemplateId}.pdf`,
        documentTitle: dispositionTrackerPageTitle,
        csvExportMode: {kind: "disabled"},
        onError: (message) => {
            showFlag({
                title: "Export failed",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });

    const showEmotoradDispositionTrackerPageExportMenu = isEmotoradDispositionTrackerTemplate === true && (search.view === "table" || search.view === "kanban");

    return (
        <>
            <TableOnlyPage
                // breadcrumbs={["Events"]}
                title={dispositionTrackerPageTitle}
                titleRightSection={
                    showEmotoradDispositionTrackerPageExportMenu ? (
                        <div
                            className={
                                isMobile
                                    ? "relative flex flex-wrap items-center justify-end gap-2 w-full min-w-0 sm:w-auto"
                                    : "relative grid grid-flow-col auto-cols-max gap-ds-space-100"
                            }
                        >
                            {isMobile ? (
                                <button
                                    type="button"
                                    onClick={() => setExportDrawerOpen(true)}
                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                    style={{
                                        borderColor: token("color.border", "#091E4224"),
                                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                                        color: token("color.icon", "#44546F"),
                                    }}
                                >
                                    <ShowMoreVerticalIcon label="Export options" />
                                </button>
                            ) : (
                                <AnankeDropdownMenu<HTMLButtonElement>
                                    placement="bottom-start"
                                    trigger={({triggerRef, ...triggerProps}) => (
                                        <button
                                            {...triggerProps}
                                            ref={triggerRef}
                                            type="button"
                                            className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                            style={{
                                                borderColor: token("color.border", "#091E4224"),
                                                backgroundColor: token("elevation.surface", "#FFFFFF"),
                                                color: token("color.icon", "#44546F"),
                                            }}
                                        >
                                            <ShowMoreVerticalIcon label="Export options" />
                                        </button>
                                    )}
                                >
                                    <AnankeDropdownItemGroup>
                                        {/* <AnankeDropdownItem
                                            onClick={() => handleExportAllRowsAsPng()}
                                            elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                            elemAfter={
                                                <ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />
                                            }
                                        >
                                            Export as PNG
                                        </AnankeDropdownItem> */}
                                        <AnankeDropdownItem
                                            onClick={() => handleExportAllRowsAsPdf()}
                                            elemBefore={
                                                <DownloadIcon
                                                    label=""
                                                    color={token("color.icon")}
                                                />
                                            }
                                            elemAfter={
                                                <ChevronRightIcon
                                                    label=""
                                                    size="small"
                                                    color={token("color.icon.subtle")}
                                                />
                                            }
                                        >
                                            Export as PDF
                                        </AnankeDropdownItem>
                                    </AnankeDropdownItemGroup>
                                </AnankeDropdownMenu>
                            )}
                        </div>
                    ) : undefined
                }
                bottomSection={<OttopilotDataViewer controller={eventsDataViewerController} />}
            />

            {isMobile && showEmotoradDispositionTrackerPageExportMenu && (
                <BottomDrawer
                    open={exportDrawerOpen}
                    setOpen={setExportDrawerOpen}
                    title="Export"
                    fitContentOnOpen={true}
                >
                    <ExportOptionsDrawerContent
                        onExportAsPng={() => {
                            handleExportAllRowsAsPng();
                            setExportDrawerOpen(false);
                        }}
                        onExportAsPdf={() => {
                            handleExportAllRowsAsPdf();
                            setExportDrawerOpen(false);
                        }}
                    />
                </BottomDrawer>
            )}

            {additionalButtonModal !== null ? (
                <EventAdditionalButtonFormModal
                    config={additionalButtonModal.config}
                    event={additionalButtonModal.event}
                    eventTemplate={eventTemplate}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    opened={true}
                    onClose={() => {
                        setAdditionalButtonModal(null);
                    }}
                    onSuccess={() => {
                        eventsDataViewerController.setCurrentlyOpenRowEngagementPanel({
                            rowEngagementType: RowEngagementType.none,
                            rowEngagementPanelType: RowEngagementPanelType.none,
                            rowEngagementData: null,
                        });
                    }}
                    queryKeyPrefix={eventsDataViewerQueryKeyPrefix}
                    workspaceUserTemplateId={workspaceUser.templateId}
                />
            ) : null}
        </>
    );
}
